import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bold, Italic, Quote, List, ListOrdered, Link2, Image as Img, ImagePlus, Youtube, Music4, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { appPrompt } from "@/components/AppDialogHost";

const AI_ACTIONS = [
  ["improve", "Improve Writing"], ["rewrite", "Rewrite"], ["shorter", "Make Shorter"],
  ["longer", "Make Longer"], ["grammar", "Fix Grammar"], ["professional", "More Professional"],
  ["conversational", "More Conversational"], ["magazine_tone", "Magazine Tone"],
  ["intro", "Generate Introduction"], ["conclusion", "Generate Conclusion"],
];

export const RichEditor = ({ value, onChange, onPickImage, onAIImage }) => {
  const ref = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current?.innerHTML || "");
  const cmd = (c, v = null) => { ref.current?.focus(); document.execCommand(c, false, v); emit(); };
  const insertHTML = (html) => { ref.current?.focus(); document.execCommand("insertHTML", false, html); emit(); };
  const saveSel = () => {
    const sel = window.getSelection();
    return sel && sel.rangeCount && ref.current?.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
  };
  const promptKeepSel = async (message) => {
    const range = saveSel();
    const u = await appPrompt(message);
    if (range) { ref.current?.focus(); const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range); }
    return u;
  };

  const addLink = async () => { const u = await promptKeepSel("Link URL:"); if (u) cmd("createLink", u); };
  const addImage = async () => {
    if (onPickImage) onPickImage((url, alt) => insertHTML(`<img src="${url}" alt="${alt || ""}" style="max-width:100%"/>`));
    else { const u = await promptKeepSel("Image URL:"); if (u) insertHTML(`<img src="${u}" style="max-width:100%"/>`); }
  };
  const addAIImage = () => {
    if (onAIImage) onAIImage((url, alt) => insertHTML(`<img src="${url}" alt="${alt || ""}" style="max-width:100%"/><p></p>`));
  };
  const addYouTube = async () => {
    const u = await promptKeepSel("YouTube URL:");
    const m = u && u.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
    if (m) insertHTML(`<div class="embed"><iframe width="100%" height="360" src="https://www.youtube.com/embed/${m[1]}" frameborder="0" allowfullscreen></iframe></div><p></p>`);
    else if (u) toast.error("Couldn't parse that YouTube URL");
  };
  const addSpotify = async () => {
    const u = await promptKeepSel("Spotify URL (track/album/playlist):");
    const m = u && u.match(/open\.spotify\.com\/(track|album|playlist|episode)\/([\w]+)/);
    if (m) insertHTML(`<div class="embed"><iframe src="https://open.spotify.com/embed/${m[1]}/${m[2]}" width="100%" height="152" frameborder="0" allow="encrypted-media"></iframe></div><p></p>`);
    else if (u) toast.error("Couldn't parse that Spotify URL");
  };
  const addAudio = async () => {
    const u = await promptKeepSel("Audio file URL (or pick from Media Library and paste):");
    if (u) insertHTML(`<audio controls src="${u}" style="width:100%"></audio><p></p>`);
  };

  const aiAction = async (action) => {
    const sel = window.getSelection();
    const hasSel = sel && !sel.isCollapsed && ref.current?.contains(sel.anchorNode);
    const text = hasSel ? sel.toString() : ref.current?.innerHTML || "";
    if (!text.trim()) { toast.error("Nothing to edit yet"); return; }
    setAiBusy(true);
    try {
      const r = await api.post("/mag/ai/edit", { text, action });
      if (action === "intro") insertAtStart(r.data.text);
      else if (action === "conclusion") { ref.current.innerHTML += `<p>${r.data.text}</p>`; emit(); }
      else if (hasSel) { document.execCommand("insertText", false, r.data.text); emit(); }
      else { ref.current.innerHTML = r.data.text; emit(); }
      toast.success("AI edit applied — review before publishing");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI edit failed"); }
    setAiBusy(false);
  };
  const insertAtStart = (t) => { ref.current.innerHTML = `<p>${t}</p>` + ref.current.innerHTML; emit(); };

  const Btn = ({ onClick, title, children, testid }) => (
    <button type="button" onClick={onClick} title={title} data-testid={testid}
      className="p-1.5 rounded hover:bg-bg-elevated text-ink-secondary hover:text-ink-primary transition">
      {children}
    </button>
  );

  return (
    <div className="rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid="mag-rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-line px-2 py-1.5 bg-bg-elevated/50">
        <select onChange={(e) => { if (e.target.value) cmd("formatBlock", e.target.value); e.target.value = ""; }}
          data-testid="mag-editor-block-select"
          className="bg-transparent text-xs font-mono border border-line rounded px-1.5 py-1 mr-1">
          <option value="">Format</option>
          <option value="h2">Heading</option>
          <option value="h3">Subheading</option>
          <option value="p">Paragraph</option>
        </select>
        <Btn onClick={() => cmd("bold")} title="Bold" testid="mag-editor-bold"><Bold className="h-4 w-4" /></Btn>
        <Btn onClick={() => cmd("italic")} title="Italic" testid="mag-editor-italic"><Italic className="h-4 w-4" /></Btn>
        <Btn onClick={() => cmd("formatBlock", "blockquote")} title="Pull quote" testid="mag-editor-quote"><Quote className="h-4 w-4" /></Btn>
        <Btn onClick={() => cmd("insertUnorderedList")} title="Bullets" testid="mag-editor-ul"><List className="h-4 w-4" /></Btn>
        <Btn onClick={() => cmd("insertOrderedList")} title="Numbered" testid="mag-editor-ol"><ListOrdered className="h-4 w-4" /></Btn>
        <Btn onClick={addLink} title="Link" testid="mag-editor-link"><Link2 className="h-4 w-4" /></Btn>
        <Btn onClick={addImage} title="Image" testid="mag-editor-image"><Img className="h-4 w-4" /></Btn>
        {onAIImage && <Btn onClick={addAIImage} title="AI Image — generate and insert here" testid="mag-editor-ai-image"><ImagePlus className="h-4 w-4 text-brand-amber" /></Btn>}
        <Btn onClick={addYouTube} title="YouTube" testid="mag-editor-youtube"><Youtube className="h-4 w-4" /></Btn>
        <Btn onClick={addSpotify} title="Spotify" testid="mag-editor-spotify"><Music4 className="h-4 w-4" /></Btn>
        <Btn onClick={addAudio} title="Audio" testid="mag-editor-audio"><Music4 className="h-4 w-4 opacity-60" /></Btn>
        <div className="ml-auto flex items-center gap-1">
          {aiBusy && <Loader2 className="h-4 w-4 animate-spin text-brand-amber" />}
          <select onChange={(e) => { if (e.target.value) aiAction(e.target.value); e.target.value = ""; }}
            disabled={aiBusy} data-testid="mag-editor-ai-select"
            className="bg-transparent text-xs font-mono border border-brand-amber/50 text-brand-amber rounded-full px-2 py-1">
            <option value="">✦ AI Actions</option>
            {AI_ACTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </div>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={emit}
        data-testid="mag-editor-body"
        className="min-h-[360px] max-h-[600px] overflow-y-auto px-5 py-4 text-sm leading-relaxed focus:outline-none prose-headings:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-4 [&_h3]:text-lg [&_h3]:font-semibold [&_blockquote]:border-l-4 [&_blockquote]:border-brand-amber [&_blockquote]:pl-3 [&_blockquote]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-brand-cyan [&_a]:underline" />
    </div>
  );
};
